/**
 * 12-Week Pure Strength Program
 *
 * Non-tactical, barbell-focused strength development. The on-ramp programs
 * (Recruit/Grunt/Specialist) are tactical-flavored — they include rucks,
 * intervals, and conditioning days. Users who pick the `strength` goal at
 * onboarding wanted a stronger squat, not to be ready for selection. This
 * program is for them: 4 days/week, one main lift per day, accessories
 * that support the main movement, no conditioning beyond a brief warm-up.
 *
 * Periodization is a two-block model loosely patterned on Wendler's 5/3/1
 * — a Volume Block (70-80% × 5) builds the work capacity foundation, then
 * an Intensity Block (80-90% × 3 with AMRAP top set) drives the peak. A
 * single deload week sits between to clear CNS fatigue before the heavier
 * reps. Testing weeks bookend the program so progress is real, not vibes.
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'pure-strength-12week',
  name: '12-Week Pure Strength',
  description: 'Barbell-focused strength program — Squat, Bench, Deadlift, and Overhead Press driven to a peak via two waved blocks. No conditioning, no rucking, just heavy iron.',
  level: 'grunt', // intermediate tier — non-tactical programs share the grunt difficulty bucket
  duration: 12,
  daysPerWeek: 4,
  goals: ['strength'],
  difficulty: 'intermediate',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
};

const periodization: ProgramPeriodizationModel = {
  style: 'block',
  phases: [
    // Week 1 + Week 12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Strength Testing',
      focus: 'Establish or re-test 5RM on the four main lifts',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
    // Weeks 2-6: Volume Block — moderate intensity, 4×5 reps.
    // Builds work capacity and grooves form before the heavier work.
    {
      weeks: [2, 3, 4, 5, 6],
      type: 'accumulation',
      name: 'Volume Block',
      focus: 'Build work capacity at moderate loads (70-80% × 5)',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.70,
        endIntensity: 0.80,
        weeklyIncrease: 0.025,
      },
      trainingParameters: {
        repRange: [5, 5],
        setsMultiplier: 1.0,
        restPeriod: '2-3min',
      },
    },
    // Week 7: Deload — CNS clears before the heavy block.
    {
      weeks: [7],
      type: 'deload',
      name: 'Recovery Deload',
      focus: 'Drop to 50-60% × 5 to flush fatigue',
      intensityMultiplier: 0.7,
      volumeMultiplier: 0.5,
      progression: {
        type: 'linear',
        startIntensity: 0.55,
        endIntensity: 0.55,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [5, 5],
        setsMultiplier: 0.5,
        restPeriod: '2-3min',
      },
    },
    // Weeks 8-11: Intensity Block — heavy 5×3 with AMRAP top set.
    // The AMRAP set on the final triple exposes whether the user is
    // ready to bump their training max next cycle.
    {
      weeks: [8, 9, 10, 11],
      type: 'intensification',
      name: 'Intensity Block',
      focus: 'Drive peak strength at 80-92% × 3, AMRAP top set',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.80,
        endIntensity: 0.92,
        weeklyIncrease: 0.04,
      },
      trainingParameters: {
        repRange: [3, 5],
        setsMultiplier: 1.0,
        restPeriod: '3-5min',
      },
    },
  ],
};

// BASE TEMPLATES

const testingDay: WorkoutTemplateDay = {
  name: 'Strength Test',
  sessionType: 'assessment',
  estimatedDuration: 85,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before max-effort lifting' },
        { name: 'Hip Opener Flow', sets: 2, reps: 10, format: 'recovery', details: 'Deep hip opening before heavy squats and deadlifts' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Wake up upper back for pressing and rowing' },
      ],
    },
    {
      name: 'Program Maxes',
      type: 'testing',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: '5RM', intensity: 'Max', details: 'Work up to a heavy 5-rep top set. 1RM estimated via Brzycki.', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: '5RM', intensity: 'Max', details: 'Pause briefly on chest, full lockout. Spotter recommended.', sessionType: 'assessment' },
        { name: 'Deadlift', sets: 1, reps: '5RM', intensity: 'Max', details: 'Conventional stance, neutral spine, full lockout.', sessionType: 'assessment' },
        { name: 'Overhead Press', sets: 1, reps: '5RM', intensity: 'Max', details: 'Strict press from rack — no leg drive. Full lockout overhead.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 10, format: 'recovery', details: 'Decompress hips and spine after heavy testing' },
      ],
    },
  ],
};

// Day 1 — Squat focus. RDL trains the hinge pattern that supports the
// squat, lunges build single-leg stability, pull-ups balance the upper-
// body pulling against the bench day's pressing volume.
const squatDay: WorkoutTemplateDay = {
  name: 'Squat',
  sessionType: 'pt',
  estimatedDuration: 80,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before heavy back squat' },
        { name: 'Hip Opener Flow', sets: 2, reps: 10, format: 'recovery', details: 'Deep hip opening — non-negotiable before squatting heavy' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Upper back activation — supports the bar bracing position' },
      ],
    },
    {
      name: 'Main Lift',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 4, reps: 5, intensity: 'progressive', details: 'Top set is the working set. Final triple in Intensity Block is AMRAP — go to RPE 9.' },
      ],
    },
    {
      name: 'Accessories',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: 8, intensity: 'RPE 7', details: 'Hinge pattern. Slight knee bend, neutral spine, feel the hamstrings load.' },
        { name: 'Walking Lunge', sets: 3, reps: 10, intensity: 'RPE 7', details: 'Per leg. Add dumbbells if bodyweight is too easy.' },
        { name: 'Pull-Ups', sets: 3, reps: 'AMRAP', format: 'bodyweight', details: 'Strict reps to RPE 8. Add weight once you hit 10+ clean reps.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 10, format: 'recovery', details: 'Decompress hips and spine — critical after heavy squatting' },
      ],
    },
  ],
};

// Day 2 — Bench focus. Incline DB press hits upper chest and shoulders
// without re-stressing the same plane as the main bench. Row matches
// horizontal pull volume to push. Triceps pushdown isolates lockout.
const benchDay: WorkoutTemplateDay = {
  name: 'Bench Press',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before pressing' },
        { name: 'Band Pull-Aparts', sets: 3, reps: 15, format: 'recovery', details: 'Critical for bench — wakes up rear delts and balances the press' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Hips support leg drive on bench' },
      ],
    },
    {
      name: 'Main Lift',
      type: 'strength',
      exercises: [
        { name: 'Bench Press', sets: 4, reps: 5, intensity: 'progressive', details: 'Pause briefly on chest. Final triple in Intensity Block is AMRAP — RPE 9.' },
      ],
    },
    {
      name: 'Accessories',
      type: 'strength',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 3, reps: 10, intensity: 'RPE 7', details: '30-45° incline. Controlled tempo, full range.' },
        { name: 'Dumbbell Row', sets: 3, reps: 10, intensity: 'RPE 7', details: 'One arm at a time, brace against bench. Squeeze the shoulder blade.' },
        { name: 'Tricep Pushdown', sets: 3, reps: 12, intensity: 'RPE 7', details: 'Lockout focus — controlled negatives, full extension at the bottom.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 8, format: 'recovery', details: 'Open hips and decompress' },
      ],
    },
  ],
};

// Day 3 — Deadlift focus. Barbell Row matches the deadlift's hinge work
// with horizontal pulling volume. Hip Thrust isolates glute drive.
// Plank Hold protects the lower back via anti-extension core work.
const deadliftDay: WorkoutTemplateDay = {
  name: 'Deadlift',
  sessionType: 'pt',
  estimatedDuration: 80,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 3, reps: 10, format: 'recovery', details: 'Critical for deadlift — spine has to be neutral and mobile' },
        { name: 'Hip Opener Flow', sets: 2, reps: 10, format: 'recovery', details: 'Open hips deeply before pulling from the floor' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Upper back lock-in supports lockout' },
      ],
    },
    {
      name: 'Main Lift',
      type: 'strength',
      exercises: [
        { name: 'Deadlift', sets: 4, reps: 5, intensity: 'progressive', details: 'Reset between reps — no touch-and-go. Final triple in Intensity Block is AMRAP — RPE 9.' },
      ],
    },
    {
      name: 'Accessories',
      type: 'strength',
      exercises: [
        { name: 'Barbell Row', sets: 3, reps: 8, intensity: 'RPE 7', details: 'Pendlay-style — pull from the floor each rep. Strict, no torso swing.' },
        { name: 'Hip Thrust', sets: 3, reps: 10, intensity: 'RPE 7', details: 'Pause at the top, squeeze glutes. Heels driven through the floor.' },
        { name: 'Plank Hold', sets: 3, reps: '45s', format: 'bodyweight', details: 'Anti-extension. Brace abs, glutes squeezed, shoulders over elbows.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress the spine after pulling' },
        { name: 'Hip Mobility Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open hips back up after the hinge work' },
      ],
    },
  ],
};

// Day 4 — Overhead Press focus. Pull-Ups give a second pull session in
// the week. Lateral raises hit the side delt isolation that compound
// pressing misses. Bicep curls round out the elbow-flexor work.
const ohpDay: WorkoutTemplateDay = {
  name: 'Overhead Press',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Band Pull-Aparts', sets: 3, reps: 15, format: 'recovery', details: 'Wake up the upper back and rear delts before pressing overhead' },
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Thoracic mobility — needed to press straight up' },
      ],
    },
    {
      name: 'Main Lift',
      type: 'strength',
      exercises: [
        { name: 'Overhead Press', sets: 4, reps: 5, intensity: 'progressive', details: 'Strict press, no leg drive. Final triple in Intensity Block is AMRAP — RPE 9.' },
      ],
    },
    {
      name: 'Accessories',
      type: 'strength',
      exercises: [
        { name: 'Pull-Ups', sets: 4, reps: 'AMRAP', format: 'bodyweight', details: 'Strict reps to RPE 8. Add weight once you hit 10+ clean reps.' },
        { name: 'Lateral Raise', sets: 3, reps: 12, intensity: 'RPE 7', details: 'Light dumbbells, controlled tempo. Side delt isolation.' },
        { name: 'Barbell Curl', sets: 3, reps: 10, intensity: 'RPE 7', details: 'Strict — no body english. Squeeze the biceps at the top.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress the spine after overhead loading' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const pureStrength12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12)
    'testing-week-day1': testingDay,
    'testing-week-day2': restDay,
    'testing-week-day3': restDay,
    'testing-week-day4': restDay,

    // Training cycle (W2-11)
    'training-day1': squatDay,
    'training-day2': benchDay,
    'training-day3': deadliftDay,
    'training-day4': ohpDay,
    'training-day5': restDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};
